/**
 * CRAFT TOWER Audio Synthesizer (Web Audio API)
 *
 * 100% programmatically generated audio without external audio assets:
 * - Dynamic mascot click audio (distinct funny animations per section; harsh cyber glitch in Cyber mode)
 * - Chat sounds (message send blip, receive chime, strike/termination alert)
 * - Safe lazy AudioContext initialization complying with browser autoplay policies
 */

import { safeGet, safeSet } from './env'

let audioCtx: AudioContext | null = null
let soundEnabled = true
let hasUserGesture = false

// Track first user interaction to cleanly unlock Web Audio without autoplay warnings
if (typeof window !== 'undefined') {
  const unlockAudio = () => {
    hasUserGesture = true
    if (audioCtx && audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => {})
    }
    window.removeEventListener('pointerdown', unlockAudio)
    window.removeEventListener('keydown', unlockAudio)
    window.removeEventListener('touchstart', unlockAudio)
  }
  window.addEventListener('pointerdown', unlockAudio, { passive: true })
  window.addEventListener('keydown', unlockAudio, { passive: true })
  window.addEventListener('touchstart', unlockAudio, { passive: true })
}

// Initialize sound preference from localStorage if available
if (typeof window !== 'undefined') {
  try {
    const saved = safeGet('local', 'craft_sound_enabled')
    if (saved !== null) {
      soundEnabled = saved === 'true'
    }
  } catch {}
}

export function isSoundEnabled(): boolean {
  return soundEnabled
}

export function setSoundEnabled(enabled: boolean): void {
  soundEnabled = enabled
  try {
    safeSet('local', 'craft_sound_enabled', String(enabled))
  } catch {}
}

export function toggleSound(): boolean {
  setSoundEnabled(!soundEnabled)
  return soundEnabled
}

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  const isUserActive =
    hasUserGesture ||
    (typeof navigator !== 'undefined' && 'userActivation' in navigator && navigator.userActivation.hasBeenActive)

  try {
    if (!audioCtx) {
      // Do not instantiate AudioContext before user interaction to prevent browser autoplay console warnings
      if (!isUserActive) return null
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext
      if (AudioCtxClass) {
        audioCtx = new AudioCtxClass()
      }
    }
    if (audioCtx && audioCtx.state === 'suspended') {
      if (isUserActive) {
        audioCtx.resume().catch(() => {})
      } else {
        // Prevent scheduling audio nodes on suspended AudioContext without user activation
        return null
      }
    }
    return audioCtx
  } catch (err) {
    console.warn('[Sound] Web Audio API unavailable:', err)
    return null
  }
}

/**
 * Distortion curve generator for Cyber mode waveshaper
 */
function makeDistortionCurve(amount = 50): Float32Array<ArrayBuffer> {
  const k = amount
  const samples = 44100
  const arrayBuffer = new ArrayBuffer(samples * 4)
  const curve = new Float32Array(arrayBuffer)
  const deg = Math.PI / 180
  for (let i = 0; i < samples; ++i) {
    const x = (i * 2) / samples - 1
    curve[i] = ((3 + k) * x * 20 * deg) / (Math.PI + k * Math.abs(x))
  }
  return curve
}

/**
 * Play programmatically generated audio when the user clicks the AI mascot bot
 */
export function playBotClickSound(section: string, isCyber = false): void {
  if (!soundEnabled) return
  const ctx = getAudioContext()
  if (!ctx) return

  const now = ctx.currentTime

  if (isCyber) {
    // -------------------------------------------------------------
    // CYBER MODE: Harsh, noisy industrial glitch burst with waveshaper
    // -------------------------------------------------------------
    try {
      const duration = 0.16
      const bufferSize = ctx.sampleRate * duration
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
      const data = buffer.getChannelData(0)
      for (let i = 0; i < bufferSize; i++) {
        // High-frequency bit-stepped white noise
        data[i] = (Math.random() * 2 - 1) * (i % 4 === 0 ? 0.9 : 0.3)
      }

      const noiseSource = ctx.createBufferSource()
      noiseSource.buffer = buffer

      // Waveshaper distortion
      const shaper = ctx.createWaveShaper()
      shaper.curve = makeDistortionCurve(90)
      shaper.oversample = '4x'

      // Bandpass resonant filter
      const filter = ctx.createBiquadFilter()
      filter.type = 'bandpass'
      filter.frequency.setValueAtTime(1400, now)
      filter.frequency.exponentialRampToValueAtTime(320, now + duration)
      filter.Q.setValueAtTime(2.5, now)

      // Gated amplitude envelope (stutter pulses)
      const gainNode = ctx.createGain()
      gainNode.gain.setValueAtTime(0.001, now)
      gainNode.gain.linearRampToValueAtTime(0.24, now + 0.01)
      gainNode.gain.setValueAtTime(0.02, now + 0.04)
      gainNode.gain.setValueAtTime(0.28, now + 0.06)
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + duration)

      // Sub-bass sawtooth crunch oscillator
      const subOsc = ctx.createOscillator()
      subOsc.type = 'sawtooth'
      subOsc.frequency.setValueAtTime(120, now)
      subOsc.frequency.exponentialRampToValueAtTime(35, now + duration)

      const subGain = ctx.createGain()
      subGain.gain.setValueAtTime(0.2, now)
      subGain.gain.exponentialRampToValueAtTime(0.001, now + duration)

      noiseSource.connect(shaper)
      shaper.connect(filter)
      filter.connect(gainNode)
      gainNode.connect(ctx.destination)

      subOsc.connect(subGain)
      subGain.connect(ctx.destination)

      noiseSource.start(now)
      noiseSource.stop(now + duration)
      subOsc.start(now)
      subOsc.stop(now + duration)
    } catch (err) {
      console.warn('[Sound] Cyber glitch playback error:', err)
    }
    return
  }

  // -----------------------------------------------------------------
  // NORMAL MODE: Funny, charming sounds customized by section block
  // -----------------------------------------------------------------
  try {
    switch (section) {
      case 'home': {
        // Section: Hero / Home -> Cartoon spring wobble ("Boing!")
        const osc = ctx.createOscillator()
        const lfo = ctx.createOscillator()
        const lfoGain = ctx.createGain()
        const gain = ctx.createGain()

        osc.type = 'sine'
        osc.frequency.setValueAtTime(240, now)
        osc.frequency.exponentialRampToValueAtTime(680, now + 0.12)
        osc.frequency.exponentialRampToValueAtTime(400, now + 0.22)

        lfo.type = 'sine'
        lfo.frequency.setValueAtTime(26, now) // rapid wobble
        lfoGain.gain.setValueAtTime(45, now)
        lfo.connect(lfoGain)
        lfoGain.connect(osc.frequency)

        gain.gain.setValueAtTime(0.001, now)
        gain.gain.linearRampToValueAtTime(0.2, now + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25)

        osc.connect(gain)
        gain.connect(ctx.destination)

        lfo.start(now)
        osc.start(now)
        lfo.stop(now + 0.25)
        osc.stop(now + 0.25)
        break
      }

      case 'services': {
        // Section: Services -> Droid / robot triple chatter (R2-D2 chirp)
        const notes = [587.33, 783.99, 1174.66] // D5 -> G5 -> D6
        notes.forEach((freq, idx) => {
          const t = now + idx * 0.04
          const osc = ctx.createOscillator()
          const gain = ctx.createGain()

          osc.type = 'triangle'
          osc.frequency.setValueAtTime(freq, t)
          osc.frequency.exponentialRampToValueAtTime(freq * 1.15, t + 0.035)

          gain.gain.setValueAtTime(0.001, t)
          gain.gain.linearRampToValueAtTime(0.16, t + 0.005)
          gain.gain.exponentialRampToValueAtTime(0.001, t + 0.035)

          osc.connect(gain)
          gain.connect(ctx.destination)

          osc.start(t)
          osc.stop(t + 0.035)
        })
        break
      }

      case 'work': {
        // Section: Work / Portfolio -> Retro arcade bubble pop / sonar bloop
        const osc = ctx.createOscillator()
        const filter = ctx.createBiquadFilter()
        const gain = ctx.createGain()

        osc.type = 'sine'
        osc.frequency.setValueAtTime(1400, now)
        osc.frequency.exponentialRampToValueAtTime(260, now + 0.1)

        filter.type = 'bandpass'
        filter.frequency.setValueAtTime(900, now)
        filter.Q.setValueAtTime(5, now)

        gain.gain.setValueAtTime(0.001, now)
        gain.gain.linearRampToValueAtTime(0.22, now + 0.01)
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.11)

        osc.connect(filter)
        filter.connect(gain)
        gain.connect(ctx.destination)

        osc.start(now)
        osc.stop(now + 0.11)
        break
      }

      case 'process': {
        // Section: Process -> Mechanical clockwork double tick
        ;[0, 0.06].forEach((offset, idx) => {
          const t = now + offset
          const osc = ctx.createOscillator()
          const gain = ctx.createGain()

          osc.type = 'triangle'
          osc.frequency.setValueAtTime(idx === 0 ? 540 : 720, t)

          gain.gain.setValueAtTime(0.001, t)
          gain.gain.linearRampToValueAtTime(0.18, t + 0.003)
          gain.gain.exponentialRampToValueAtTime(0.001, t + 0.03)

          osc.connect(gain)
          gain.connect(ctx.destination)

          osc.start(t)
          osc.stop(t + 0.03)
        })
        break
      }

      case 'about': {
        // Section: About -> Cheerful melodic "Woop-woop" cartoon slide
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()

        osc.type = 'sine'
        osc.frequency.setValueAtTime(320, now)
        osc.frequency.exponentialRampToValueAtTime(540, now + 0.08)
        osc.frequency.exponentialRampToValueAtTime(420, now + 0.12)
        osc.frequency.exponentialRampToValueAtTime(660, now + 0.2)

        gain.gain.setValueAtTime(0.001, now)
        gain.gain.linearRampToValueAtTime(0.19, now + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22)

        osc.connect(gain)
        gain.connect(ctx.destination)

        osc.start(now)
        osc.stop(now + 0.22)
        break
      }

      case 'contact': {
        // Section: Contact -> Friendly reception desk bell ping (crystal chime)
        const freqs = [1046.5, 2093] // C6 & harmonic octave
        freqs.forEach((freq, idx) => {
          const osc = ctx.createOscillator()
          const gain = ctx.createGain()

          osc.type = 'sine'
          osc.frequency.setValueAtTime(freq, now)

          gain.gain.setValueAtTime(0.001, now)
          gain.gain.linearRampToValueAtTime(idx === 0 ? 0.2 : 0.08, now + 0.005)
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35)

          osc.connect(gain)
          gain.connect(ctx.destination)

          osc.start(now)
          osc.stop(now + 0.35)
        })
        break
      }

      default: {
        // Fallback default pleasant greeting blip
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()

        osc.type = 'sine'
        osc.frequency.setValueAtTime(440, now)
        osc.frequency.exponentialRampToValueAtTime(780, now + 0.08)

        gain.gain.setValueAtTime(0.001, now)
        gain.gain.linearRampToValueAtTime(0.18, now + 0.01)
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.09)

        osc.connect(gain)
        gain.connect(ctx.destination)

        osc.start(now)
        osc.stop(now + 0.09)
        break
      }
    }
  } catch (err) {
    console.warn('[Sound] Normal mascot sound playback error:', err)
  }
}

/**
 * Modern soft blip when user sends a chat message
 */
export function playChatSendSound(): void {
  if (!soundEnabled) return
  const ctx = getAudioContext()
  if (!ctx) return

  try {
    const now = ctx.currentTime
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = 'sine'
    osc.frequency.setValueAtTime(460, now)
    osc.frequency.exponentialRampToValueAtTime(840, now + 0.06)

    gain.gain.setValueAtTime(0.001, now)
    gain.gain.linearRampToValueAtTime(0.15, now + 0.008)
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.065)

    osc.connect(gain)
    gain.connect(ctx.destination)

    osc.start(now)
    osc.stop(now + 0.065)
  } catch (err) {
    console.warn('[Sound] Chat send audio error:', err)
  }
}

/**
 * Pleasant two-tone harmonic chime when admin or AI reply arrives
 */
export function playChatReceiveSound(): void {
  if (!soundEnabled) return
  const ctx = getAudioContext()
  if (!ctx) return

  try {
    const now = ctx.currentTime
    // Note 1: E5 (659Hz)
    const osc1 = ctx.createOscillator()
    const gain1 = ctx.createGain()
    osc1.type = 'sine'
    osc1.frequency.setValueAtTime(659.25, now)
    gain1.gain.setValueAtTime(0.001, now)
    gain1.gain.linearRampToValueAtTime(0.15, now + 0.005)
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.12)
    osc1.connect(gain1)
    gain1.connect(ctx.destination)
    osc1.start(now)
    osc1.stop(now + 0.12)

    // Note 2: A5 (880Hz) with 50ms delay
    const t2 = now + 0.05
    const osc2 = ctx.createOscillator()
    const gain2 = ctx.createGain()
    osc2.type = 'sine'
    osc2.frequency.setValueAtTime(880, t2)
    gain2.gain.setValueAtTime(0.001, t2)
    gain2.gain.linearRampToValueAtTime(0.18, t2 + 0.005)
    gain2.gain.exponentialRampToValueAtTime(0.001, t2 + 0.22)
    osc2.connect(gain2)
    gain2.connect(ctx.destination)
    osc2.start(t2)
    osc2.stop(t2 + 0.22)
  } catch (err) {
    console.warn('[Sound] Chat receive audio error:', err)
  }
}

/**
 * Low warning tone when strike warnings or session termination occurs
 */
export function playChatAlertSound(): void {
  if (!soundEnabled) return
  const ctx = getAudioContext()
  if (!ctx) return

  try {
    const now = ctx.currentTime
    const osc = ctx.createOscillator()
    const filter = ctx.createBiquadFilter()
    const gain = ctx.createGain()

    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(260, now)
    osc.frequency.exponentialRampToValueAtTime(140, now + 0.18)

    filter.type = 'lowpass'
    filter.frequency.setValueAtTime(450, now)

    gain.gain.setValueAtTime(0.001, now)
    gain.gain.linearRampToValueAtTime(0.2, now + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.19)

    osc.connect(filter)
    filter.connect(gain)
    gain.connect(ctx.destination)

    osc.start(now)
    osc.stop(now + 0.19)
  } catch (err) {
    console.warn('[Sound] Chat alert audio error:', err)
  }
}

/**
 * High-clarity triple harmonic ping for tab inactivity / background tab alerts
 * Designed to cut gently through background noise without harshness
 */
export function playChatInactivityPingSound(): void {
  if (!soundEnabled) return
  const ctx = getAudioContext()
  if (!ctx) return

  try {
    const now = ctx.currentTime
    const notes = [659.25, 830.61, 987.77] // E5, G#5, B5 (Major triad)
    notes.forEach((freq, i) => {
      const noteTime = now + i * 0.07
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.setValueAtTime(freq, noteTime)
      gain.gain.setValueAtTime(0.001, noteTime)
      gain.gain.linearRampToValueAtTime(0.16, noteTime + 0.006)
      gain.gain.exponentialRampToValueAtTime(0.001, noteTime + 0.24)
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start(noteTime)
      osc.stop(noteTime + 0.24)
    })
  } catch (err) {
    console.warn('[Sound] Chat inactivity ping audio error:', err)
  }
}
